/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './ProgramBenefitsPage.scss';

import React, { Fragment, Component } from 'react';
import { connect } from 'react-redux';

import HouseholdInformationCard from 'components/layouts/HouseholdInformationCard/HouseholdInformationCard';
import HouseholdDataProvider from 'components/shared/DataProviders/HouseholdDataProvider';
import ExclusiveBenefitsCards from './ExclusiveBenefitsCards';
import GetPromotedCard from './GetPromotedCard';
import { LinkRpsAccountsCard } from './LinkRpsAccountsCard';
import {
  getView,
  hasWorkplaceRetirementAccounts
} from 'state/view/ViewSelectors';
import { getRpsSubscriptionInformation } from 'services/Rest';
import {
  getAssetId,
  getNextTierId,
  getNextTierMinimum,
  hasAssetId,
  isEPSClient
} from 'state/household/HouseholdInformationSelectors';
import {
  isEligibleForRps,
  isSubscribedToRps
} from 'state/LinkRpsAccounts/LinkRpsAccountsSelectors';
import Content from 'services/Content';
import { contentConverter, programBenefitsContent } from 'services/Content';
import {
  benefitsInsightsLink,
  lifeEventsManagerLink
} from 'services/Externals';
import MobileBenefitsDropdown from './MobileBenefitsDropdown';
import { ReactComponent as FinancialConsultant } from 'assets/financial-consultant.svg';
import { REQUEST_STATES } from 'state/requests/RequestStatusReducer';
import { GLOBALS } from 'services/globals';
import { getHasReceivedHousehold, getRequestStatus } from 'state/requests/RequestStatusSelectors';
import { isError } from 'support/RequestSupport';
import { AEM } from 'services/Externals';
import { CONTENT_TYPES } from 'services/ContentManagement/CMSUtils';
import ContentFragment from 'services/ContentManagement/ContentFragment/ContentFragment';
import { Redirect } from 'react-router-dom';
import { retailTracEnabled } from 'services/FeatureFlags';

const HouseholdInformationCardWithData = HouseholdDataProvider(
  HouseholdInformationCard
);
const ExclusiveBenefitsCardsWithData = HouseholdDataProvider(
  ExclusiveBenefitsCards
);

const getDescription = (text) => {
  const contentHtml = contentConverter.makeHtml(text);
  return <span dangerouslySetInnerHTML={{ __html: contentHtml }} />;
};

const ProgramBenefitsTile = ({
  image: Image,
  title,
  description,
  footer,
  isFullWidth,
  isMobileLayout
}) => {
  const fullWidth = isFullWidth ? ' full-width' : '';
  return isMobileLayout ? (
    <MobileBenefitsDropdown
      title={title}
      description={description}
      footer={footer}
    />
  ) : (
    <Fragment>
      <div className={`benefits-tile-container${fullWidth}`}>
        {Image && <Image className="benefits-tile-img" />}
        <p className="benefits-tile-title">{title}</p>
        {description && (
          <div className="benefits-tile-description">
            {getDescription(description)}
          </div>
        )}
        {footer && <div className="benefits-tile-footer">{footer}</div>}
      </div>
    </Fragment>
  );
};

ProgramBenefitsTile.defaultProps = {
  isFullWidth: false,
  image: null,
  footer: null
};

const allClientBenefitsFooter = {
  financialConsultant: <p>Call 1-800-401-1819</p>,
  insights: <a href={benefitsInsightsLink}>Explore Insights</a>,
  lifeEvents: <a href={lifeEventsManagerLink}>Get Support</a>
};

class ProgramBenefitsPage extends Component {
  componentDidMount() {
    const { requestStatus } = this.props;
    const { rpsSubscriptionInformation } = requestStatus;

    if (rpsSubscriptionInformation !== REQUEST_STATES.received) {
      getRpsSubscriptionInformation();
    }
  }

  render() {
    const {
      view,
      nextTierId,
      nextTierMinAmount,
      isEPSClient,
      hasAssetTierId,
      hasReceivedHousehold,
      hasWorkplaceRetirementAccounts,
      isSubscribedToRps,
      isEligibleForRps
    } = this.props;

    const { isMobileLayout } = view;

    const showRPSTile = hasWorkplaceRetirementAccounts && isEligibleForRps;

    const hasRpsInfoError = isError('rpsSubscriptionInformation');

    return (
      <Fragment>
        {retailTracEnabled() && !hasAssetTierId && hasReceivedHousehold && (
            <Redirect to="/program-benefits-ineligible" />
        )}
        <div
          data-testid={'program-benefits-page'}
          className="program-benefits-page">
          <div className="program-benefits-header">
            <div className="program-benefits-title">
              <h1>T. Rowe Price Summit Program</h1>
              <p className="program-benefits-subtitle">
                The Summit Program is designed to equip you with exclusive
                benefits to help you achieve better outcomes. As you save more
                for retirement, college, and other goals you can achieve higher
                membership status and gain entry to more and more benefits.
              </p>
              <b className="exclusive-client-benefits">
                Exclusive client benefits grow along with your Summit balance.
              </b>
            </div>
            <div className="program-card">
              <HouseholdInformationCardWithData
                showBalance={!isEPSClient}
                styleOverride
              />
            </div>
          </div>
          <ExclusiveBenefitsCardsWithData view={view} />
          <p className="benefits-page-title">All Client Benefits</p>
          <div className="all-client-benefits">
            <ProgramBenefitsTile
              image={FinancialConsultant}
              title="Financial Consultant"
              description={Content.benefitsFinancialConsultant}
              footer={allClientBenefitsFooter.financialConsultant}
              isMobileLayout={isMobileLayout}
            />
            <div className="wrapper-benefits-tile">
              <ProgramBenefitsTile
                title="Cost Savings"
                description={Content.benefitsCostSavings}
                isMobileLayout={isMobileLayout}
              />
              <ProgramBenefitsTile
                title="T. Rowe Price Insights"
                description={Content.benefitsInsights}
                footer={allClientBenefitsFooter.insights}
                isMobileLayout={isMobileLayout}
              />
            </div>
            <ProgramBenefitsTile
              title="Dedicated Life Event Support"
              description={Content.dedicatedLifeEventSupport}
              footer={allClientBenefitsFooter.lifeEvents}
              isFullWidth={true}
              isMobileLayout={isMobileLayout}
            />
          </div>
          {!isEPSClient && (
            <GetPromotedCard
              nextTierId={nextTierId}
              nextTierMinAmount={nextTierMinAmount}
              isMobileLayout={isMobileLayout}
              showRPSTile={showRPSTile}
              hasRpsInfoError={hasRpsInfoError}
              subscribed={isSubscribedToRps}
              shouldDisable
            />
          )}
          {isEPSClient && showRPSTile && !hasRpsInfoError && (
            <div className="workplace-retirement-plan-accounts-container">
              <p className="benefits-page-title">Add to your Summit balance.</p>
              <div className="grow-balance-card long">
                <LinkRpsAccountsCard
                  subscribed={isSubscribedToRps}
                  shouldDisable
                />
              </div>
            </div>
          )}
          <p className="benefits-page-title">Additional Information</p>
          <div className="additional-info-container">
            <div className="customer-service-rps-container">
              <div className="customer-service-container">
                <p className="customer-service-header">Questions?</p>
                <p
                  className="customer-service-text"
                  dangerouslySetInnerHTML={{
                    __html: programBenefitsContent.customerServiceText
                  }}></p>
                <div className="customer-service-footer">
                  <a
                    href={`${GLOBALS.AAM_UI_EXTERNAL_BASE_URL}/personal-investing/about/client-benefits/faqs.html`}>
                    View FAQs
                  </a>
                </div>
              </div>
            </div>
            <div className="membership-levels-container">
              <p className="membership-levels-header">Membership Levels</p>
              <div className="membership-levels-content">
                <p className="membership-levels-sub-header">
                  General Services (All Investors)
                </p>
                <div className="membership-level">
                  {getDescription(Content.allClientBenefits)}
                </div>
                <div className="membership-level">
                  <b>Select Services ($250,000 or more)</b>
                  {getDescription(Content.selectBenefits)}
                </div>

                <div className="membership-level">
                  <b>Personal Services ($500,000 or more)</b>
                  {getDescription(Content.psBenefits)}
                </div>

                <div className="membership-level">
                  <b>Enhanced Personal Services ($1 Million or more)</b>
                  {getDescription(Content.epsBenefits)}
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="program-benefits-footer-terms">
              <ContentFragment
                cmsUrl={AEM.FRAGMENTS.summitIClass}
                styleIdentifier="summit-disclosure iclass"
                contentKey="main"
                dataTransform={(root) => {
                  return root['value'];
                }}
                contentType={CONTENT_TYPES.HTML}
              />
            </div>
          </div>
        </div>
      </Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  assetId: getAssetId(state),
  view: getView(state),
  nextTierId: getNextTierId(state),
  nextTierMinAmount: getNextTierMinimum(state),
  isEPSClient: isEPSClient(state),
  requestStatus: getRequestStatus(state),
  hasAssetTierId: hasAssetId(state),
  hasReceivedHousehold: getHasReceivedHousehold(state),
  hasWorkplaceRetirementAccounts: hasWorkplaceRetirementAccounts(state),
  isSubscribedToRps: isSubscribedToRps(state),
  isEligibleForRps: isEligibleForRps(state)
});

export default connect(mapStateToProps)(ProgramBenefitsPage);
